/**
 * Ludicrous Gateway - Multi-agent LLM API Gateway
 *
 * Routes Claude Code requests to multiple providers based on
 * content analysis (token count, keywords, patterns).
 */

import { loadConfig } from "./config/index.js";
import { createServer } from "./server/http-server.js";
import { initLogger, getLogger } from "./utils/logger.js";

// Export types
export type {
  GatewayConfig,
  ProviderConfig,
  RoutingConfig,
  RoutingRuleConfig,
  HealthConfig,
} from "./types/config.js";

export type {
  MessagesRequest,
  MessagesResponse,
  Message,
  ContentBlock,
} from "./types/messages.js";

export type {
  Provider,
  ProviderInfo,
  ProviderHealth,
  ProviderRequestContext,
  StreamHandler,
} from "./types/providers.js";

export type {
  ContentAnalysis,
  RoutingDecision,
  CompiledRoutingRule,
  Router,
  ContentAnalyzer,
} from "./types/routing.js";

// Export classes and functions
export { GatewayServer, createServer } from "./server/http-server.js";
export { loadConfig, validateConfig } from "./config/index.js";
export { DefaultRouter, createRouter } from "./routing/router.js";
export {
  DefaultContentAnalyzer,
  createAnalyzerFromRules,
} from "./analyzers/content-analyzer.js";
export { HealthTracker } from "./health/health-tracker.js";
export { CircuitBreaker, CircuitBreakerManager } from "./health/circuit-breaker.js";
export { RateLimiter, RateLimiterManager } from "./health/rate-limiter.js";

// Export provider factories
export { createAnthropicProvider } from "./providers/anthropic.js";
export { createOpenAIProvider, OPENAI_MODELS, DEFAULT_OPENAI_ALIASES } from "./providers/openai.js";
export { createZaiProvider } from "./providers/zai.js";
export { createOllamaProvider } from "./providers/ollama.js";
export { createKimiProvider } from "./providers/kimi.js";
export { createDeepSeekProvider, DEEPSEEK_MODELS } from "./providers/deepseek.js";
export { createMistralProvider, MISTRAL_MODELS, DEFAULT_MISTRAL_ALIASES } from "./providers/mistral.js";

// Export utilities
export { Logger, initLogger, getLogger, createLogger } from "./utils/logger.js";
export { MetricsCollector, initMetrics, getMetrics } from "./utils/metrics.js";

/**
 * Main entry point when run directly
 */
async function main(): Promise<void> {
  try {
    // Load configuration
    const config = loadConfig({ useDefaults: true });

    // Initialize logger
    initLogger(config.logging);
    const logger = getLogger();

    logger.info("Starting ludicrous-gateway", {
      listen: config.server.listen,
      providers: config.providers.filter((p) => p.enabled).map((p) => p.name),
      rules: config.routing.rules.length,
    });

    // Create and start server
    const server = await createServer(config);

    // Handle shutdown
    const shutdown = async (signal: string) => {
      logger.info(`Received ${signal}, shutting down...`);
      await server.stop();
      process.exit(0);
    };

    process.on("SIGINT", () => void shutdown("SIGINT"));
    process.on("SIGTERM", () => void shutdown("SIGTERM"));

    // Handle uncaught errors
    process.on("uncaughtException", (error) => {
      logger.error("Uncaught exception", { error: error.message });
      process.exit(1);
    });

    process.on("unhandledRejection", (reason) => {
      logger.error("Unhandled rejection", {
        reason: reason instanceof Error ? reason.message : String(reason),
      });
    });
  } catch (error) {
    console.error(
      "Failed to start:",
      error instanceof Error ? error.message : "Unknown error"
    );
    process.exit(1);
  }
}

// Run if executed directly (not imported)
const isDirectExecution =
  import.meta.url === `file://${process.argv[1]}` ||
  process.argv[1]?.endsWith("/ludicrous-gateway") ||
  process.argv[1]?.endsWith("dist/index.js");

if (isDirectExecution) {
  void main();
}
